import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Save, ChevronDown, ChevronRight, Calendar, Dumbbell, X, Copy, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_EXERCISE = { name: "", set1: "", set2: "", set3: "", set4: "", rpe: "", feedback: "" };

export default function WorkoutLogTable({ token, clientId, isTrainer = false }) {
  const [logs, setLogs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planDays, setPlanDays] = useState([]);
  const [showPlanPicker, setShowPlanPicker] = useState(false);

  // New log form
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bodyPart, setBodyPart] = useState("");
  const [weekNum, setWeekNum] = useState("");
  const [dayNum, setDayNum] = useState("");
  const [exercises, setExercises] = useState([{ ...EMPTY_EXERCISE }]);

  const apiBase = isTrainer
    ? axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } })
    : axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const basePath = isTrainer ? `/admin/clients/${clientId}` : "/client";

  const load = useCallback(() => {
    apiBase.get(`${basePath}/workout-logs`).then(r => setLogs(r.data)).catch(() => {});
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  // Fetch the assigned workout plan to enable "Load from plan"
  useEffect(() => {
    if (!isTrainer) return;
    apiBase.get(`${basePath}/workout-plan`)
      .then((r) => {
        const plan = r.data?.plan || r.data;
        const days = plan?.weeks?.[0]?.days || plan?.days || [];
        setPlanDays(Array.isArray(days) ? days : []);
      })
      .catch(() => setPlanDays([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, isTrainer]);

  const loadFromPlanDay = (day, weekIndex = 0, dayIndex = 0) => {
    if (!day) return;
    setWeekNum(String(weekIndex + 1));
    setDayNum(String(dayIndex + 1));
    // Build a body-part summary like "Chest & Triceps (Push Day)"
    setBodyPart(day.focus ? `${day.focus}` : day.name || "");
    const exs = (day.exercises || []).map((ex) => {
      // Map saved sets/reps into the first-set slot as a hint, leave fields blank for the trainer to fill in actual numbers
      const groups = (Array.isArray(ex.set_groups) && ex.set_groups.length > 0)
        ? ex.set_groups
        : ((ex.sets || ex.reps) ? [{ sets: ex.sets, reps: ex.reps }] : []);
      // Pre-fill set1..setN placeholders with target like "x12", "x10"
      const placeholders = {};
      const repsList = [];
      groups.forEach((g) => {
        const s = parseInt(g.sets) || 0;
        for (let i = 0; i < s; i++) repsList.push(g.reps || "");
      });
      // Fill up to 4 set slots with the planned target reps (trainer fills in weight × done-reps after each set)
      ["set1", "set2", "set3", "set4"].forEach((k, idx) => {
        if (repsList[idx]) placeholders[k] = `× ${repsList[idx]}`;
      });
      return { ...EMPTY_EXERCISE, name: ex.name || "", ...placeholders };
    });
    setExercises(exs.length ? exs : [{ ...EMPTY_EXERCISE }]);
    setShowPlanPicker(false);
    toast.success(`Loaded ${day.exercises?.length || 0} exercise${day.exercises?.length === 1 ? '' : 's'} from plan`);
  };

  const updateExercise = (i, field, value) => {
    const e = [...exercises];
    e[i] = { ...e[i], [field]: value };
    setExercises(e);
  };

  const addExercise = () => setExercises([...exercises, { ...EMPTY_EXERCISE }]);
  const removeExercise = (i) => setExercises(exercises.filter((_, idx) => idx !== i));

  const saveLog = async () => {
    if (!bodyPart.trim()) { toast.error("Enter body part"); return; }
    if (!exercises.some(e => e.name.trim())) { toast.error("Add at least one exercise"); return; }
    setSaving(true);
    try {
      await apiBase.post(`${basePath}/workout-log`, {
        date,
        body_part: bodyPart,
        week: weekNum ? parseInt(weekNum) : null,
        day: dayNum ? parseInt(dayNum) : null,
        exercises,
      });
      toast.success("Workout logged!");
      setShowForm(false);
      setBodyPart("");
      setWeekNum("");
      setDayNum("");
      setExercises([{ ...EMPTY_EXERCISE }]);
      load();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const updateLogExercises = async (logId, updatedExercises) => {
    try {
      await apiBase.put(`${basePath}/workout-log/${logId}`, { exercises: updatedExercises });
      toast.success("Updated!");
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteLog = async (logId) => {
    if (!isTrainer) return;
    if (!window.confirm("Delete this workout log?")) return;
    try {
      await apiBase.delete(`${basePath}/workout-log/${logId}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const duplicateLog = (log) => {
    setDate(new Date().toISOString().slice(0, 10));
    setBodyPart(log.body_part);
    setExercises(log.exercises.map(e => ({ ...e, set1: "", set2: "", set3: "", set4: "", rpe: "", feedback: "" })));
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="workout-log-table">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm uppercase tracking-wider font-bold text-white flex items-center gap-2">
          <Dumbbell size={16} className="text-gg-red" /> Workout Log
        </h3>
        {isTrainer && (
          <Button onClick={() => setShowForm(!showForm)} size="sm" className={`${showForm ? "bg-zinc-700 hover:bg-zinc-600" : "bg-gg-red hover:bg-gg-red-hover"} text-white rounded-lg text-xs font-heading uppercase tracking-wider`}>
            {showForm ? <><X size={12} className="mr-1" /> Cancel</> : <><Plus size={12} className="mr-1" /> New Log</>}
          </Button>
        )}
      </div>

      {/* New Log Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="border border-gg-red/20 rounded-xl bg-gg-black p-4 space-y-3">
            <div className="flex gap-2 flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Date</label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-xs" />
              </div>
              <div className="w-16">
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Week</label>
                <Input type="number" min="1" placeholder="1" value={weekNum} onChange={e => setWeekNum(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-xs text-center" data-testid="log-week-input" />
              </div>
              <div className="w-16">
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Day</label>
                <Input type="number" min="1" placeholder="1" value={dayNum} onChange={e => setDayNum(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-xs text-center" data-testid="log-day-input" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Body Part</label>
                <Input placeholder="e.g. Chest & Shoulder" value={bodyPart} onChange={e => setBodyPart(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-xs" />
              </div>
            </div>

            {/* Load from saved Workout Plan */}
            {planDays.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPlanPicker(!showPlanPicker)}
                  data-testid="load-from-plan-btn"
                  className="text-[10px] font-heading uppercase tracking-wider text-gg-red hover:text-white border border-dashed border-gg-red/40 hover:border-gg-red rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-colors"
                >
                  <FileDown size={11} /> Load from Workout Plan {showPlanPicker ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                </button>
                {showPlanPicker && (
                  <div className="absolute z-30 left-0 top-full mt-1 w-full sm:w-72 bg-gg-surface border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-72 overflow-y-auto">
                    {planDays.map((day, di) => (
                      <button
                        key={di}
                        type="button"
                        onClick={() => loadFromPlanDay(day, 0, di)}
                        data-testid={`plan-day-${di}`}
                        className="w-full text-left px-3 py-2 hover:bg-gg-red/10 border-b border-white/5 last:border-0"
                      >
                        <p className="text-gg-red text-[9px] font-heading uppercase tracking-widest">Week 1 · Day {di + 1}</p>
                        <p className="text-white text-xs font-heading uppercase tracking-wider mt-0.5">{day.name || `Day ${di + 1}`}</p>
                        <p className="text-zinc-500 text-[10px] mt-0.5">{day.focus || `${(day.exercises || []).length} exercises`}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Exercise table header */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="text-[8px] uppercase tracking-wider text-zinc-500">
                    <th className="text-left pb-2 pr-2 w-[22%]">Exercise</th>
                    <th className="text-left pb-2 px-1 w-[14%]">Set 1</th>
                    <th className="text-left pb-2 px-1 w-[14%]">Set 2</th>
                    <th className="text-left pb-2 px-1 w-[14%]">Set 3</th>
                    <th className="text-left pb-2 px-1 w-[14%]">Set 4</th>
                    <th className="text-left pb-2 px-1 w-[6%]">RPE</th>
                    <th className="text-left pb-2 px-1 w-[14%]">Feedback</th>
                    <th className="w-[2%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {exercises.map((ex, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-1.5 pr-2"><Input placeholder="DB Chest Press" value={ex.name} onChange={e => updateExercise(i, "name", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="7.5kg x12" value={ex.set1} onChange={e => updateExercise(i, "set1", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="7.5kg x12" value={ex.set2} onChange={e => updateExercise(i, "set2", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="7.5kg x8" value={ex.set3} onChange={e => updateExercise(i, "set3", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="" value={ex.set4} onChange={e => updateExercise(i, "set4", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="6" value={ex.rpe} onChange={e => updateExercise(i, "rpe", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5 px-1"><Input placeholder="Notes..." value={ex.feedback} onChange={e => updateExercise(i, "feedback", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                      <td className="py-1.5"><button onClick={() => removeExercise(i)} className="text-zinc-600 hover:text-red-400"><X size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={addExercise} className="text-[10px] text-gg-red hover:underline flex items-center gap-1"><Plus size={10} /> Add Exercise</button>
              <Button onClick={saveLog} disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg text-xs">
                <Save size={12} className="mr-1" /> {saving ? "..." : "Save Log"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logs List */}
      {logs.length === 0 && !showForm && (
        <p className="text-zinc-600 text-xs text-center py-8">{isTrainer ? "No workout logs yet. Click \"New Log\" to start tracking." : "Your trainer hasn't logged any sessions yet."}</p>
      )}

      {logs.map((log) => (
        <LogCard key={log.id} log={log} isTrainer={isTrainer} expanded={expanded === log.id}
          onToggle={() => setExpanded(expanded === log.id ? null : log.id)}
          onUpdate={(exs) => updateLogExercises(log.id, exs)}
          onDelete={() => deleteLog(log.id)}
          onDuplicate={() => duplicateLog(log)} />
      ))}
    </div>
  );
}

function LogCard({ log, isTrainer, expanded, onToggle, onUpdate, onDelete, onDuplicate }) {
  const [editing, setEditing] = useState(false);
  const [exercises, setExercises] = useState(log.exercises || []);

  const updateExercise = (i, field, value) => {
    const e = [...exercises];
    e[i] = { ...e[i], [field]: value };
    setExercises(e);
  };

  const handleSave = () => {
    onUpdate(exercises);
    setEditing(false);
  };

  return (
    <div className="border border-white/10 rounded-xl bg-gg-surface overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 sm:p-4 text-left hover:bg-white/5 transition-colors" data-testid={`log-${log.id}`}>
        {expanded ? <ChevronDown size={14} className="text-zinc-500 shrink-0" /> : <ChevronRight size={14} className="text-zinc-500 shrink-0" />}
        <Calendar size={14} className="text-gg-red shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-white text-xs font-semibold">{log.date}</span>
          {(log.week || log.day) && (
            <span className="text-zinc-500 text-[10px] ml-2 font-heading uppercase tracking-wider">
              {log.week ? `W${log.week}` : ""}{log.week && log.day ? "·" : ""}{log.day ? `D${log.day}` : ""}
            </span>
          )}
          <span className="text-gg-red text-[10px] ml-2 font-heading uppercase tracking-wider">{log.body_part}</span>
        </div>
        <span className="text-zinc-600 text-[10px]">{log.exercises?.length || 0} exercises</span>
        {log.created_by === "member" && <span className="text-[8px] px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-full">Member</span>}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2">
              <div className="flex items-center gap-2 justify-end">
                {isTrainer && (
                  <>
                    <button onClick={onDuplicate} className="text-[9px] text-zinc-500 hover:text-white flex items-center gap-1 transition-colors" title="Use as template for new log">
                      <Copy size={10} /> Duplicate
                    </button>
                    {!editing && (
                      <button onClick={() => setEditing(true)} className="text-[9px] text-gg-red hover:underline">Edit</button>
                    )}
                    {editing && (
                      <button onClick={handleSave} className="text-[9px] text-green-400 hover:underline flex items-center gap-1"><Save size={10} /> Save</button>
                    )}
                    <button onClick={onDelete} className="text-[9px] text-zinc-600 hover:text-red-400 transition-colors"><Trash2 size={11} /></button>
                  </>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[650px]">
                  <thead>
                    <tr className="text-[8px] uppercase tracking-wider text-zinc-600 border-b border-white/5">
                      <th className="text-left pb-2 pr-2">Exercise</th>
                      <th className="text-left pb-2 px-1">Set 1</th>
                      <th className="text-left pb-2 px-1">Set 2</th>
                      <th className="text-left pb-2 px-1">Set 3</th>
                      <th className="text-left pb-2 px-1">Set 4</th>
                      <th className="text-left pb-2 px-1">RPE</th>
                      <th className="text-left pb-2 px-1">Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exercises.map((ex, i) => (
                      <tr key={i} className="border-t border-white/5">
                        {editing ? (
                          <>
                            <td className="py-1.5 pr-2"><Input value={ex.name} onChange={e => updateExercise(i, "name", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.set1} onChange={e => updateExercise(i, "set1", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.set2} onChange={e => updateExercise(i, "set2", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.set3} onChange={e => updateExercise(i, "set3", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.set4} onChange={e => updateExercise(i, "set4", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.rpe} onChange={e => updateExercise(i, "rpe", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                            <td className="py-1.5 px-1"><Input value={ex.feedback} onChange={e => updateExercise(i, "feedback", e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-[11px] px-2" /></td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 pr-2 text-white text-xs font-medium">{ex.name}</td>
                            <td className="py-2 px-1 text-zinc-300 text-[11px]">{ex.set1 || "—"}</td>
                            <td className="py-2 px-1 text-zinc-300 text-[11px]">{ex.set2 || "—"}</td>
                            <td className="py-2 px-1 text-zinc-300 text-[11px]">{ex.set3 || "—"}</td>
                            <td className="py-2 px-1 text-zinc-300 text-[11px]">{ex.set4 || "—"}</td>
                            <td className="py-2 px-1 text-gg-red text-[11px] font-semibold">{ex.rpe || "—"}</td>
                            <td className="py-2 px-1 text-zinc-500 text-[10px] italic">{ex.feedback || "—"}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
